import React, { useState } from 'react'
import type { FullPublicPotentialProfileFragment } from 'apollo-gql'
import { currentLanguageIdVar, currentMarketIdVar } from 'graphql/reactive'
import { useTranslation } from 'lib/i18n'
import { darkGrey, lightGrey, white } from 'lib/theme'
import trackPotentialProfileSubscribed from 'lib/tracking/avo/trackPotentialProfileSubscribed'
import nl2br from 'react-nl2br'

import CmsPageBasic from 'components/CmsPage/CmsPageBasic'
import EmailSignupForm from 'components/EmailSignupForm/EmailSignupForm'
import Heading from 'components/Heading/Heading'
import PotentialProfileTag from 'components/PotentialProfileTag/PotentialProfileTag'
import ProfileDetails from 'components/ProfileDetails/ProfileDetails'
import ProfilePageHeader from 'components/ProfilePageHeader/ProfilePageHeader'
import ProfileStepsSection from 'components/ProfileStepsSection/ProfileStepsSection'
import SharingButtons from 'components/SharingButtons/SharingButtons'
import WaveWrapper from 'components/WaveWrapper/WaveWrapper'
import Wrapper from 'components/Wrapper/Wrapper'
import useCustomerData from 'hooks/useCustomerData'
import useResponsive from 'hooks/useResponsive'
import useSubscribeToChanges from 'hooks/useSubscribeToChanges'

import PotentialProfileMessageForm from './PotentialProfileMessageForm'

import styles from './PotentialProfilePage.module.css'

type ProfileDescriptionProps = {
  description: string
}

const ProfileDescription: React.VFC<ProfileDescriptionProps> = ({
  description,
}) => {
  if (!description) return null

  return (
    <div className={styles.profileHeaderDescription}>{nl2br(description)}</div>
  )
}

const DesktopHeaderContent: React.FC = ({ children }) => (
  <div className={styles.profileDesktopHeaderContent}>{children}</div>
)

const MobileSteps = () => (
  <WaveWrapper backgroundColor={white} waveColor={lightGrey} bottom>
    <ProfileStepsSection color={darkGrey} />
  </WaveWrapper>
)

type ProfileDetailsMobileProps = {
  description: string
}

const ProfileDetailsMobile: React.FC<ProfileDetailsMobileProps> = ({
  description,
  children,
}) => (
  <WaveWrapper backgroundColor={darkGrey} waveColor={white} bottom>
    <div className={styles.details}>
      <Wrapper>
        <ProfileDescription description={description} />
        {children}
      </Wrapper>
    </div>
  </WaveWrapper>
)

type PotentialProfileSignupProps = {
  profile: FullPublicPotentialProfileFragment
  done: boolean
  setDone: (done: boolean) => void
  setEmail: (email: string) => void
}

const PotentialProfileSignup: React.VFC<PotentialProfileSignupProps> = ({
  profile,
  done,
  setDone,
  setEmail,
}) => {
  const { t } = useTranslation()
  const customerData = useCustomerData()

  const [subscribe, { loading }] = useSubscribeToChanges({
    onCompleted: () => {
      trackPotentialProfileSubscribed({
        potentialProfile: profile,
      })
      setDone(true)
    },
  })

  return (
    <EmailSignupForm
      loading={loading}
      done={done}
      onFinish={(input: { email: string }) => {
        if (customerData) {
          customerData.logEvent('potential-profile-subscribe', {
            email: input.email,
            profileId: profile.id,
          })
          customerData.modify({ email: input.email })
        }
        setEmail(input.email)
        subscribe({ ...input, potentialProfileId: profile.id })
      }}
      text={t(
        'PotentialProfileSignup.infoText',
        'Be the first to know when {{profileName}} is here. Signup and we will get back to you!',
        {
          profileName: profile?.name,
        },
      )}
      heading={null}
      couponCode={null}
      isPopup={false}
    />
  )
}

type PotentialProfileHeadingProps = {
  profileName: string
}

const PotentialProfileHeading: React.VFC<PotentialProfileHeadingProps> = ({
  profileName,
}) => {
  const { t } = useTranslation()

  return (
    <Heading level="h3" color="light">
      <h3>
        {t(
          'PotentialProfileSignup.title',
          'Let {{profileName}} know you are waiting',
          { profileName },
        )}
      </h3>
    </Heading>
  )
}

type PotentialProfilePageProps = {
  profile: FullPublicPotentialProfileFragment
  loading: boolean
}

const PotentialProfilePage: React.VFC<PotentialProfilePageProps> = ({
  profile,
  loading,
}) => {
  const { t } = useTranslation()
  const { description } = profile || {}

  const marketId = currentMarketIdVar()
  const languageId = currentLanguageIdVar()

  const [subscribeDone, setSubscribeDone] = useState(false)
  const [email, setEmail] = useState<string>(null)
  const { isMobile } = useResponsive()

  const desktopHeaderContent = (
    <DesktopHeaderContent>
      <ProfileDescription description={description} />
      <PotentialProfileTag
        profileName={profile?.name}
        style={{ margin: '30px 0' }}
      />
      <PotentialProfileHeading profileName={profile?.name} />
      {!subscribeDone ? (
        <PotentialProfileSignup
          profile={profile}
          done={subscribeDone}
          setDone={setSubscribeDone}
          setEmail={setEmail}
        />
      ) : (
        <PotentialProfileMessageForm profile={profile} email={email} />
      )}
    </DesktopHeaderContent>
  )

  const header = (
    <ProfilePageHeader
      profile={profile}
      loading={loading}
      isMobile={isMobile}
      desktopHeaderContent={desktopHeaderContent}
    />
  )

  const relativeUrl = `/${marketId}/${languageId}/profile/${profile?.slug}`

  return (
    <>
      <ProfileDetails header={header}>
        {isMobile && (
          <>
            <Wrapper>
              <PotentialProfileTag
                profileName={profile?.name}
                style={{ margin: '0 0 10px' }}
              />
            </Wrapper>
            <ProfileDetailsMobile description={description}>
              <div style={{ marginTop: 40 }}>
                <PotentialProfileHeading profileName={profile?.name} />
                {!subscribeDone ? (
                  <PotentialProfileSignup
                    profile={profile}
                    done={subscribeDone}
                    setDone={setSubscribeDone}
                    setEmail={setEmail}
                  />
                ) : (
                  <>
                    <PotentialProfileMessageForm
                      profile={profile}
                      email={email}
                    />
                    <div style={{ marginTop: 30 }}>
                      <Heading level="h4" color="light">
                        <h4>
                          {t(
                            'PotentialProfilePage.shareHeading',
                            'Share the profile and get your friends onboard',
                          )}
                        </h4>
                      </Heading>
                      <SharingButtons
                        relativeUrl={relativeUrl}
                        showIcons={false}
                        talentSlug={null}
                      />
                    </div>
                  </>
                )}
              </div>
            </ProfileDetailsMobile>
          </>
        )}
      </ProfileDetails>

      {isMobile && <MobileSteps />}

      <Wrapper edgeToEdge style={{ marginTop: 40 }}>
        <CmsPageBasic slug="potential-profile" />
      </Wrapper>
    </>
  )
}

export default PotentialProfilePage
